import { useEffect, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Table as RBTable,
    Button,
    Modal,
    Form,
    InputGroup,
    Spinner,
    Badge,
    Pagination,
    Card
} from 'react-bootstrap';
import Swal from 'sweetalert2';
//Components
import NavBar from '../NavBar/Navbar';
//Alerts 
import ErrorAlert from '../Alerts/ErrorAlert';
import SuccessAlert from '../Alerts/SuccessAlert';

//API
import { DeliveryFeeRulesFetchAPI } from '../../api/DeliveryFeeRules';
import { TaxesFetchAPI } from '../../api/Taxes';
import ZoneMapEditor from '../../new_version/components/Delivery/ZoneMapEditor';
import '../../new_version/components/Delivery/delivery_rules.css';

// Constants for form options
const CHANNELS = ['ecommerce', 'kiosk', 'pos'];
const FEE_TYPES = [{ label: 'Monto', value: 'fixed' }, { label: 'Porcentaje', value: 'percent' }, { label: "Kilometro", value: "kilometer" }, { label: "Zona", value: "zone" }];

const EMPTY_FORM = {
    id: null,
    name: '',
    business_id: '',
    branchoffice_id: '',
    channel: 'ecommerce',
    min_subtotal_to_waive: 0,
    fee_type: 'fixed',
    fee_amount: 0,
    fee_percent: 0,
    currency: 'USD',
    active_from: '',
    active_to: '',
    is_active: true,
    taxable: true,
    priority: 100,
    // Scheduling
    allow_immediate: true,
    allow_scheduled: false,
    lead_time_minutes: 30,
    days_ahead: 7,
    window_start: null,
    window_end: null,
    slot_minutes: 60,
    zones: []
};

function money(n) {
    if (n === null || n === undefined || isNaN(n)) return '-';
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toInputDateTime(value) {
    // Converts ISO or date to 'YYYY-MM-DDThh:mm' in local
    if (!value) return '';
    const d = new Date(value);
    const pad = (x) => String(x).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function fromInputDateTime(value) {
    // Returns the iso date string (or null if its empty)
    if (!value) return null;
    const d = new Date(value);
    return d.toISOString();
}

export default function DeliveryManager() {
    const [rules, setRules] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [selectedTaxesCreate, setSelectedTaxesCreate] = useState({});
    const [assignedTaxes, setAssignedTaxes] = useState([]);
    const [assigning, setAssigning] = useState(false);
    const [removing, setRemoving] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 20; // To the pagination

    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const business = JSON.parse(localStorage.getItem('user'))?.business;
    const token = localStorage.getItem('x-access-token');
    const branchoffices = JSON.parse(localStorage.getItem('user'))?.branchoffices || [];


    // helper to build the default selection map
    const toSel = (list = []) =>
        (Array.isArray(list) ? list : []).reduce((acc, t) => {
            acc[t.id] = { checked: false, priority: 100, override_rate: '' };
            return acc;
        }, {});

    //Function to get the taxes 
    const getTaxes = async () => {
        try {
            const response = await TaxesFetchAPI.getTaxes(token);
            const list = response?.data?.data || response?.data || response || [];
            const arr = Array.isArray(list) ? list : [];
            setTaxes(arr);
            setSelectedTaxesCreate(toSel(arr));  // ← init para “crear”
        } catch (err) {
            setTaxes([]);
            setSelectedTaxesCreate({});
        }
    };
    //Function to load the rules
    const loadRules = async () => {
        try {
            setLoading(true);
            const res = await DeliveryFeeRulesFetchAPI.getDeliveryFeeRulesByBusiness(business.id, token);
            console.log(res);
            const data = Array.isArray(res?.data.data) ? res.data.data : res;
            setRules(data || []);
            setTotal((data || []).length);
        } catch (err) {
            console.error(err);
            ErrorAlert(err?.response?.data?.error || err.response.data.error || 'No se pudo cargar las reglas', 'Advertencia', "warning");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadRules(); getTaxes(); }, []);

    //Function to open the modal to create a new rule
    const openCreate = () => {
        setAssignedTaxes([]); // no hay impuestos aún
        setEditing({
            ...EMPTY_FORM,
            business_id: business?.id ?? '',
            active_from: toInputDateTime(new Date()),
            active_to: toInputDateTime('2999-12-31T23:59:59Z')
        });
        setSelectedTaxesCreate(toSel(taxes)); // ← checklist para crear
        setShowForm(true);
    }
    //Function to open the modal to edit a rule
    const openEdit = async (row) => {
        setEditing({
            ...EMPTY_FORM,
            ...row,
            is_active: !!row.is_active,
            taxable: !!row.taxable,
            active_from: toInputDateTime(row.active_from),
            active_to: toInputDateTime(row.active_to)
        });
        try {
            const det = await DeliveryFeeRulesFetchAPI.getDeliveryFeeRuleDetails(row.id, token);
            const rule = det?.data?.data || det?.data || det || {};
            const assigned = rule.taxes || rule.delivery_taxes || [];
            setAssignedTaxes(Array.isArray(assigned) ? assigned : []);
        } catch {
            setAssignedTaxes([]);
        }
        setSelectedTaxesCreate(toSel(taxes)); // ← init for "create"
        setShowForm(true);
    }
    //Function to delete a rule
    const handleDelete = async (row) => {
        Swal.fire({
            title: '¿Eliminar regla?',
            text: `¿Eliminar la regla "${row.name}"? Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await DeliveryFeeRulesFetchAPI.deleteDeliveryFeeRule(row.id, token);
                    await loadRules();
                    SuccessAlert('Eliminada', 'La regla ha sido eliminada.');
                } catch (err) {
                    console.error(err);
                    ErrorAlert(err?.response?.data?.error || err.response.data.error || 'No se pudo eliminar la regla', 'Advertencia', "warning");
                }
            }
        });
    }

    //Function to save the form (create or update)
    const saveForm = async () => {
        if (!editing) return;
        if (editing.fee_type === 'zone' && !(editing.zones || []).length) {
            ErrorAlert('Dibuja o importa al menos una zona antes de guardar', 'Advertencia', 'warning');
            return;
        }
        try {
            console.log(assignedTaxes)
            setSaving(true);
            const payload = {
                name: editing.name?.trim() || 'Sin nombre',
                business_id: Number(editing.business_id || 0),
                branchoffice_id: editing.branchoffice_id ? Number(editing.branchoffice_id) : null,
                channel: editing.channel,
                min_subtotal_to_waive: Number(editing.min_subtotal_to_waive || 0),
                fee_type: editing.fee_type, // 'fixed' | 'percent' | 'kilometer'
                fee_amount: Number(editing.fee_amount || 0),
                fee_percent: Number(editing.fee_percent || 0),
                currency: (editing.currency || 'USD').toUpperCase(),
                active_from: fromInputDateTime(editing.active_from),
                active_to: fromInputDateTime(editing.active_to),
                is_active: editing.is_active ? 1 : 0,
                taxable: editing.taxable ? 1 : 0,
                taxes: editing.taxable
                    ? Object.entries(selectedTaxesCreate)
                        .filter(([_, v]) => v.checked)
                        .map(([tax_rate_id]) => Number(tax_rate_id))
                    : [],
                // si es kilometer:
                base_km_included: Number(editing.base_km_included || 0),
                per_km_rate: Number(editing.per_km_rate || 0),
                min_fee: editing.min_fee !== '' && editing.min_fee != null ? Number(editing.min_fee) : null,
                max_fee: editing.max_fee !== '' && editing.max_fee != null ? Number(editing.max_fee) : null,
                km_rounding: editing.km_rounding || 'nearest',
                distance_source: editing.distance_source || 'haversine',
                distance_multiplier: Number(editing.distance_multiplier || 1),
                priority: editing.priority,
                zones: editing.fee_type === 'zone'
                    ? (editing.zones || []).map(z => ({ name: z.name, price: Number(z.price), color: z.color, priority: Number(z.priority || 100), polygon: z.polygon }))
                    : undefined,
                allow_immediate: editing.allow_immediate ? 1 : 0,
                allow_scheduled: editing.allow_scheduled ? 1 : 0,
                lead_time_minutes: editing.allow_scheduled
                    ? Number(editing.lead_time_minutes || 0)
                    : 0,

                days_ahead: editing.allow_scheduled
                    ? Number(editing.days_ahead || 0)
                    : 0,

                window_start: editing.allow_scheduled
                    ? editing.window_start
                    : null,

                window_end: editing.allow_scheduled
                    ? editing.window_end
                    : null,

                slot_minutes: editing.allow_scheduled
                    ? Number(editing.slot_minutes || 0)
                    : 0
            };

            // If editing has id, update. Otherwise, create new
            if (editing.id) {
                await DeliveryFeeRulesFetchAPI.updateDeliveryFeeRule(editing.id, payload, token);
                SuccessAlert('Regla de delivery actualizada', 'La regla ha sido actualizada.');
            } else {
                await DeliveryFeeRulesFetchAPI.createDeliveryFeeRule(payload, token);
                SuccessAlert('Regla de delivery Creada', 'La regla ha sido creada.');
            }
            // Refresh list and close modal
            await loadRules();
        } catch (err) {
            ErrorAlert(err?.response?.data?.error || err.response.data.error || 'No se pudo guardar la regla', 'Advertencia', "warning");
        } finally {
            setSaving(false);
        }
    }

    //Function to assign the selected taxes to the rule
    const assigningTaxesToRule = async () => {
        try {
            setAssigning(true);
            const toAssign = Object.entries(selectedTaxesCreate)
                .filter(([_, v]) => v.checked)
                .map(([tax_rate_id, v]) => (Number(tax_rate_id)));

            if (!toAssign.length) return SuccessAlert('Sin selección', 'Selecciona al menos un impuesto.');
            await DeliveryFeeRulesFetchAPI.asignTaxesToDeliveryFeeRule({ delivery_fee_id: editing.id, taxes: toAssign }, token);
            const det = await DeliveryFeeRulesFetchAPI.getDeliveryFeeRuleDetails(editing.id, token);
            const rule = det?.data?.data || det?.data || det || {};
            const assigned = rule.taxes || rule.delivery_taxes || [];
            // update assigned and reset selection
            setAssignedTaxes(Array.isArray(assigned) ? assigned : []);
            SuccessAlert('Impuestos agregados', 'Se asignaron los impuestos a la regla.');
            setSelectedTaxesCreate(toSel(taxes));
        } catch (e) {
            ErrorAlert(e?.response?.data?.error || 'No se pudieron asignar impuestos', 'Advertencia', 'warning');
        } finally {
            setAssigning(false);
        }
    }
    // Pagination calculations
    const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
    const rulesArray = Array.isArray(rules) ? rules : [];
    const start = (page - 1) * pageSize;
    const end = page * pageSize;
    const pageRows = rulesArray.slice(start, end);

    useEffect(() => {
        if (showForm && !editing?.id) {
            setSelectedTaxesCreate(toSel(taxes));
        }
    }, [taxes, showForm, editing?.id]);

    return (
        <>
            {/** Navbar */}
            <NavBar titlePage="Delivery Manager" route="config/new_delivery" />
            {/** Content  */}
            <Container className="py-3">
                <Row className="mb-3">
                    <Col>
                        <Button onClick={openCreate} variant="primary">+ Nueva regla</Button>
                    </Col>
                </Row>

                <Card>
                    <Card.Body>
                        <div className="table-responsive">
                            <RBTable striped hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Activo</th>
                                        <th>Nombre</th>
                                        <th>Sucursal</th>
                                        <th>Canal</th>
                                        <th>Umbral gratis</th>
                                        <th>Tipo</th>
                                        <th>Monto</th>
                                        <th>%</th>
                                        <th>Distancia</th>
                                        <th>Moneda</th>
                                        <th>Programado</th>
                                        <th>Prioridad</th>
                                        <th>Calculara impuesto</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={14} className="text-center py-4"><Spinner animation="border" /></td>
                                        </tr>
                                    ) : pageRows.length ? (
                                        pageRows.map((r) => (
                                            <tr key={"rule" + r.id}>
                                                <td>{Number(r.is_active) ? <Badge bg="success">Sí</Badge> : <Badge bg="secondary">No</Badge>}</td>
                                                <td>{r.name}</td>
                                                <td>{r.branchoffice_name ?? 'Todas las sucursales'}</td>
                                                <td><Badge bg="secondary">{r.channel}</Badge></td>
                                                <td>${money(r.min_subtotal_to_waive)}</td>
                                                <td>{r.fee_type === "fixed" ? "Por monto" : r.fee_type === "percent" ? "Por porcentaje" : r.fee_type === "zone" ? "Por zona" : 'Por kilometro'}</td>
                                                <td>{r.fee_type === 'fixed' ? `$${money(r.fee_amount)}` : '-'}</td>
                                                <td>{r.fee_type === 'percent' ? `${r.fee_percent}%` : '-'}</td>
                                                <td>{r.fee_type === 'kilometer' ? (
                                                    <>
                                                        <div>${money(r.per_km_rate ?? 0)} por km</div>
                                                    </>
                                                ) : r.fee_type === 'zone' ? `${r.zones?.length || 0} zona${(r.zones?.length || 0) === 1 ? '' : 's'}` : '-'}</td>
                                                <td>{r.currency}</td>
                                                <td>{Number(r.allow_scheduled) ? 'Sí' : 'No'}</td>
                                                <td>{r.priority}</td>

                                                <td>{Number(r.taxable) ? 'Sí' : 'No'}</td>
                                                <td className="text-end">
                                                    <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(r)}>Editar</Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r)}>Eliminar</Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={14} className="text-center text-muted py-4">Sin reglas registradas</td>
                                        </tr>
                                    )}
                                </tbody>
                            </RBTable>
                        </div>

                        <div className="d-flex justify-content-end mt-2">
                            <Pagination size="sm">
                                <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
                                <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                                <Pagination.Item active>{page}</Pagination.Item>
                                <Pagination.Next disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                                <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            </Container>

            {/* Modal create/edit */}
            <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editing?.id ? 'Editar regla' : 'Nueva regla'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editing && (
                        <Form>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Label>Nombre</Form.Label>
                                    <Form.Control
                                        value={editing.name}
                                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                        placeholder="Regla envío e-commerce"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Sucursal</Form.Label>
                                    <Form.Select
                                        value={editing.branchoffice_id}
                                        onChange={(e) => setEditing({ ...editing, branchoffice_id: e.target.value })}
                                    >
                                        <option value='null'>Todas las sucursales</option>
                                        {branchoffices.map((branch) => (
                                            <option key={"select-branch" + branch.id} value={branch.branchoffice_id}>{branch.branchoffice_name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={3}>
                                    <Form.Label>Canal</Form.Label>
                                    <Form.Select
                                        value={editing.channel}
                                        onChange={(e) => setEditing({ ...editing, channel: e.target.value })}
                                    >
                                        {CHANNELS.map((c) => (
                                            <option key={"select-channel" + c} value={c}>{c}</option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={3}>
                                    <Form.Label>Umbral envío gratis</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={editing.min_subtotal_to_waive}
                                            onChange={(e) => setEditing({ ...editing, min_subtotal_to_waive: e.target.value })}
                                        />
                                    </InputGroup>
                                </Col>

                                <Col md={3}>
                                    <Form.Label>Tipo</Form.Label>
                                    <Form.Select
                                        value={editing.fee_type}
                                        onChange={(e) => setEditing({ ...editing, fee_type: e.target.value })}
                                    >
                                        {FEE_TYPES.map((t) => (
                                            <option key={"select-fee-type" + t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                {editing.fee_type === 'percent' && (
                                    <Col md={3}>
                                        <Form.Label>% Porcentaje</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={editing.fee_percent}
                                                onChange={(e) => setEditing({ ...editing, fee_percent: e.target.value })}
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Col>
                                )}

                                {editing.fee_type === 'fixed' && (
                                    <Col md={3}>
                                        <Form.Label>Monto fijo</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>$</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={editing.fee_amount}
                                                onChange={(e) => setEditing({ ...editing, fee_amount: e.target.value })}
                                            />
                                        </InputGroup>
                                    </Col>
                                )}

                                {editing.fee_type === 'kilometer' && (
                                    <Col md={3}>
                                        <Form.Label>Fuente para el cálculo del costo de distancia</Form.Label>
                                        <Form.Select
                                            value={editing.distance_source || 'haversine'}
                                            onChange={(e) => setEditing({ ...editing, distance_source: e.target.value })}
                                        >
                                            <option value="null">Seleccione...</option>
                                            <option value="asap">Asap</option>
                                            <option value="uber_direct">Uber Direct</option>
                                            <option value="haversine">SmartPay</option>
                                            <option value="tookan">Tookan</option>
                                        </Form.Select>
                                    </Col>
                                )}

                                {editing.fee_type === 'kilometer' && editing.distance_source === "haversine" && (
                                    <>

                                        <Col md={3}>
                                            <Form.Label>Kilómetros incluidos</Form.Label>
                                            <Form.Control type="number" step="0.01"
                                                value={editing.base_km_included ?? 0}
                                                onChange={(e) => setEditing({ ...editing, base_km_included: e.target.value })}
                                            />
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>$ por km</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>$</InputGroup.Text>
                                                <Form.Control type="number" step="0.01"
                                                    value={editing.per_km_rate ?? 0}
                                                    onChange={(e) => setEditing({ ...editing, per_km_rate: e.target.value })}
                                                />
                                            </InputGroup>
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Fee mínimo</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>$</InputGroup.Text>
                                                <Form.Control type="number" step="0.01"
                                                    value={editing.min_fee ?? ''} placeholder="Opcional"
                                                    onChange={(e) => setEditing({ ...editing, min_fee: e.target.value })}
                                                />
                                            </InputGroup>
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Fee máximo</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>$</InputGroup.Text>
                                                <Form.Control type="number" step="0.01"
                                                    value={editing.max_fee ?? ''} placeholder="Opcional"
                                                    onChange={(e) => setEditing({ ...editing, max_fee: e.target.value })}
                                                />
                                            </InputGroup>
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Multiplicador</Form.Label>
                                            <Form.Control type="number" step="0.01"
                                                value={editing.distance_multiplier ?? 1}
                                                onChange={(e) => setEditing({ ...editing, distance_multiplier: e.target.value })}
                                            />
                                        </Col>
                                    </>
                                )}

                                {editing.fee_type === 'zone' && (
                                    <Col md={12}>
                                        <hr />
                                        <h6 className="mb-2">Zonas de cobertura</h6>
                                        <ZoneMapEditor
                                            zones={editing.zones || []}
                                            onChange={(zones) => setEditing({ ...editing, zones })}
                                        />
                                    </Col>
                                )}


                                <Col md={3}>
                                    <Form.Label>Moneda</Form.Label>
                                    <Form.Control
                                        value={editing.currency}
                                        onChange={(e) => setEditing({ ...editing, currency: e.target.value.toUpperCase() })}
                                    />
                                </Col>

                                {/* DELIVERY SCHEDULING */}
                                <Col md={12}>
                                    <hr />
                                    <h6>Disponibilidad del delivery</h6>
                                </Col>

                                <Col md={4}>
                                    <Form.Check
                                        type="switch"
                                        label="Permitir delivery inmediato"
                                        checked={!!editing.allow_immediate}
                                        onChange={(e) => setEditing({ ...editing, allow_immediate: e.target.checked })}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Form.Check
                                        type="switch"
                                        label="Permitir delivery programado"
                                        checked={!!editing.allow_scheduled}
                                        onChange={(e) => setEditing({ ...editing, allow_scheduled: e.target.checked })}
                                    />
                                </Col>

                                {editing.allow_scheduled && (
                                    <>
                                        <Col md={3}>
                                            <Form.Label>Anticipo (min)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editing.lead_time_minutes}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, lead_time_minutes: e.target.value })
                                                }
                                            />
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Desde</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={editing.window_start || ''}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, window_start: e.target.value })
                                                }
                                            />
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Hasta</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={editing.window_end || ''}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, window_end: e.target.value })
                                                }
                                            />
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Días a futuro</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editing.days_ahead}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, days_ahead: e.target.value })
                                                }
                                            />
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label>Tiempo preparación (min)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editing.slot_minutes}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, slot_minutes: e.target.value })
                                                }
                                            />
                                        </Col>
                                    </>
                                )}

                                <Col md={3} className="d-flex align-items-center">
                                    <Form.Check
                                        type="switch"
                                        id="is_active"
                                        label="Activo"
                                        checked={!!editing.is_active}
                                        onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                                    />
                                </Col>

                                <Col md={3} className="d-flex align-items-center">
                                    <Form.Check
                                        type="switch"
                                        id="taxable"
                                        label="Calculara impuesto"
                                        checked={!!editing.taxable}
                                        onChange={(e) => setEditing({ ...editing, taxable: e.target.checked })}
                                    />
                                </Col>
                                {editing.taxable && (
                                    <>
                                        {editing.id ? (
                                            // Edition mode: list of assigned taxes + add new ones
                                            <Row className="g-3 mt-2">
                                                <Col md={12}>
                                                    <h6 className="mb-2">Impuestos asignados</h6>
                                                    <div className="table-responsive">
                                                        <RBTable bordered size="sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Impuesto</th>
                                                                    <th>Porcentaje (%)</th>
                                                                    <th className="text-end">Acción</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {assignedTaxes?.length ? assignedTaxes.map((t) => (
                                                                    <tr key={`assigned-${t.tax_id || t.id}`}>
                                                                        <td>{t.tax}</td>
                                                                        <td>{t.percentage}</td>
                                                                        <td className="text-end">
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-danger"
                                                                                disabled={removing === (t.tax_id || t.id)}
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        setRemoving(t.tax_id || t.id);
                                                                                        await DeliveryFeeRulesFetchAPI.removeTaxFromDeliveryRule(
                                                                                            { delivery_fee_id: editing.id, tax_id: (t.tax_id || t.id) }, token
                                                                                        );
                                                                                        const det = await DeliveryFeeRulesFetchAPI.getDeliveryFeeRuleDetails(editing.id, token);
                                                                                        const rule = det?.data?.data || det?.data || det || {};
                                                                                        const assigned = rule.taxes || rule.delivery_taxes || [];
                                                                                        setAssignedTaxes(Array.isArray(assigned) ? assigned : []);
                                                                                        SuccessAlert('Impuesto eliminado', 'Se quitó el impuesto de la regla.');
                                                                                    } catch (e) {
                                                                                        ErrorAlert(e?.response?.data?.error || 'No se pudo quitar el impuesto', 'Advertencia', 'warning');
                                                                                    } finally {
                                                                                        setRemoving(null);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Eliminar
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )) : (
                                                                    <tr><td colSpan={5} className="text-center text-muted">Sin impuestos asignados</td></tr>
                                                                )}
                                                            </tbody>
                                                        </RBTable>
                                                    </div>
                                                </Col>

                                                <Col md={12} className="mt-3">
                                                    <h6 className="mb-2">Agregar impuestos</h6>
                                                    <div className="table-responsive">
                                                        <RBTable bordered size="sm">
                                                            <thead>
                                                                <tr>
                                                                    <th></th>
                                                                    <th>Impuesto</th>
                                                                    <th>Porcentaje (%)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {taxes?.length ? taxes
                                                                    .filter(tx => !(assignedTaxes || []).some(a => (a.tax_id || a.id) === tx.id))
                                                                    .map((tx) => {
                                                                        const sel = selectedTaxesCreate[tx.id] || { checked: false, priority: 100, override_rate: '' };
                                                                        return (
                                                                            <tr key={`add-${tx.id}`}>
                                                                                <td>
                                                                                    <Form.Check
                                                                                        type="checkbox"
                                                                                        checked={!!sel.checked}
                                                                                        onChange={(e) => setSelectedTaxesCreate(prev => ({
                                                                                            ...prev,
                                                                                            [tx.id]: { ...sel, checked: e.target.checked }
                                                                                        }))}
                                                                                    />
                                                                                </td>
                                                                                <td>{tx.tax}</td>
                                                                                <td>{tx.percentage}</td>
                                                                            </tr>
                                                                        );
                                                                    }) : (
                                                                    <tr><td colSpan={5} className="text-center text-muted">No hay impuestos disponibles</td></tr>
                                                                )}
                                                            </tbody>
                                                        </RBTable>
                                                    </div>
                                                    <div className="text-end">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            disabled={assigning}
                                                            onClick={async () => { assigningTaxesToRule(); }}
                                                        >
                                                            Agregar seleccionados
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        ) : (
                                            // CREATION MODE: simple checklist
                                            <Row className="g-2 mt-2">
                                                <Col md={12}><h6 className="mb-2">Impuestos a asignar (opcional)</h6></Col>
                                                <Col md={12}>
                                                    <div className="table-responsive">
                                                        <RBTable bordered size="sm">
                                                            <thead>
                                                                <tr>
                                                                    <th></th>
                                                                    <th>Impuesto</th>
                                                                    <th>Rate (tabla)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {taxes?.length ? taxes.map((tx) => {
                                                                    const sel = selectedTaxesCreate[tx.id] || { checked: false, priority: 100, override_rate: '' };
                                                                    return (
                                                                        <tr key={`create-${tx.id}`}>
                                                                            <td>
                                                                                <Form.Check
                                                                                    type="checkbox"
                                                                                    checked={!!sel.checked}
                                                                                    onChange={(e) => setSelectedTaxesCreate(prev => ({
                                                                                        ...prev,
                                                                                        [tx.id]: { ...sel, checked: e.target.checked }
                                                                                    }))}
                                                                                />
                                                                            </td>
                                                                            <td>{tx.tax}</td>
                                                                            <td>{tx.percentage}</td>
                                                                        </tr>
                                                                    );
                                                                }) : (
                                                                    <tr><td colSpan={5} className="text-center text-muted">No hay impuestos</td></tr>
                                                                )}
                                                            </tbody>
                                                        </RBTable>
                                                    </div>
                                                    <small className="text-muted">Los impuestos seleccionados se asignarán automáticamente al crear la regla.</small>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                )}

                            </Row>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowForm(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={saveForm} disabled={saving}>
                        {saving ? 'Guardando…' : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
